/** @jsxImportSource preact */
import type { ComponentChildren } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

import { contrastRatio } from "./theme";

export type WidgetCharacterVariant = "solid" | "watercolor";

export type WidgetCharacterMotion =
  | "idle"
  | "quiet"
  | "still"
  | "working"
  | "looking"
  | "speaking"
  | "waiting"
  | "asleep"
  | "dozing"
  | "ack"
  | "done"
  | "blocked";

/** 完成动作的时长；会话反应按它计时归位。 */
export const CHARACTER_DONE_MS = 450;
/** 白眼睛和水彩身体至少要有的对比度，和非文字元素的要求一致。 */
const WATERCOLOR_EYE_CONTRAST = 3;

/** 商家选了水彩玻璃，但这个颜色压不住白眼睛时退回实色。 */
export function characterVariantFor(
  requested: WidgetCharacterVariant,
  color: string,
): WidgetCharacterVariant {
  return requested === "watercolor" &&
    contrastRatio(color, "#FFFFFF") >= WATERCOLOR_EYE_CONTRAST
    ? "watercolor"
    : "solid";
}

interface WidgetCharacterProps {
  color: string;
  eyeColor: string;
  size?: number;
  radius?: string;
  showEyes?: boolean;
  motion?: WidgetCharacterMotion;
  /** 水彩：主题色在眼睛后最浓、向边缘变淡并模糊背后页面；眼睛固定白色，提示正文用加深的主题色，忽略 `eyeColor`。 */
  variant?: WidgetCharacterVariant;
  /** 主动提示或回复提示的正文：背景与尾巴拉长，眼睛和文字保持原比例。给了 `label` 就用它当按钮名称。 */
  prompt?: { text: ComponentChildren; label?: string; activate?: () => void };
}

/** 页面级鼠标反馈只接管空闲角色；真实处理与倾听动作保持优先。 */
function useCharacterGaze(enabled: boolean) {
  const root = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const character = root.current;
    if (!character || !enabled) return;
    const page = character.ownerDocument;
    const body = character.querySelector<HTMLElement>(".gn-character-body");
    const eyes = character.querySelectorAll<HTMLElement>(
      ".gn-character-eyes > i",
    );
    if (!body) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reset = () => {
      character.removeAttribute("data-gazing");
      body.style.removeProperty("transform");
      eyes.forEach((eye) => eye.style.removeProperty("translate"));
    };
    const move = (event: Event) => {
      if (
        !(event instanceof MouseEvent) ||
        !("pointerType" in event) ||
        event.pointerType !== "mouse" ||
        reduced.matches ||
        !pointer.matches ||
        document.hidden ||
        character.parentElement?.closest(
          '[inert], [hidden], [aria-hidden="true"], [data-held]',
        )
      ) {
        reset();
        return;
      }
      const rect = character.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      const distance = Math.max(260, Math.hypot(dx, dy));
      // 按逻辑尺寸计算，避免 Settings 整体缩放被重复计入。
      const range =
        Math.min(character.offsetWidth, character.offsetHeight) * 0.07;
      character.setAttribute("data-gazing", "");
      body.style.transform = `perspective(600px) rotateY(${String((dx / distance) * 12)}deg) rotateX(${String((-dy / distance) * 12)}deg)`;
      eyes.forEach((eye) => {
        eye.style.translate = `${String((dx / distance) * range)}px ${String((dy / distance) * range)}px`;
      });
    };
    page.addEventListener("pointermove", move, true);
    page.addEventListener("pointerleave", reset);
    page.addEventListener("pointerdown", move, true);
    reduced.addEventListener("change", reset);
    pointer.addEventListener("change", reset);
    document.addEventListener("visibilitychange", reset);
    window.addEventListener("blur", reset);
    return () => {
      reset();
      page.removeEventListener("pointermove", move, true);
      page.removeEventListener("pointerleave", reset);
      page.removeEventListener("pointerdown", move, true);
      reduced.removeEventListener("change", reset);
      pointer.removeEventListener("change", reset);
      document.removeEventListener("visibilitychange", reset);
      window.removeEventListener("blur", reset);
    };
  }, [enabled]);
  return root;
}

/** 纯展示角色：动作由所在位置和真实会话状态决定，不持有会话或演示计时器。 */
export function WidgetCharacter({
  color,
  eyeColor,
  size = 56,
  radius = "32%",
  showEyes = true,
  motion = "still",
  variant = "solid",
  prompt,
}: WidgetCharacterProps) {
  const root = useCharacterGaze(
    showEyes && (motion === "idle" || motion === "quiet"),
  );
  return (
    <span
      ref={root}
      aria-hidden={prompt === undefined ? true : undefined}
      class="gn-character"
      data-motion={motion}
      data-variant={variant}
      data-prompt={prompt === undefined ? undefined : "body"}
      style={{
        "--gn-character-size": `${String(size)}px`,
        "--gn-character-color": color,
        "--gn-character-eye": eyeColor,
        "--gn-character-radius": radius,
      }}
    >
      <span class="gn-character-body">
        {showEyes && (
          <span class="gn-character-eyes" aria-hidden="true">
            <i />
            <i />
          </span>
        )}
      </span>
      {prompt !== undefined && (
        <button
          class="gn-character-copy gn-proactive-copy"
          aria-expanded={false}
          aria-label={prompt.label}
          type="button"
          onClick={prompt.activate}
        >
          {prompt.text}
        </button>
      )}
    </span>
  );
}

const TAIL_SHAPE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M0 0H24V22.5Q24 24.8 22.5 23.2C18 20 18 14 14 14Z'/%3E%3C/svg%3E")`;

/** 原型的身体、短尾和完整 10 秒待机序列；眼睛反向缩放，避免跟随身体拉伸。 */
export const CHARACTER_STYLES = `
.gn-character { display:inline-block; flex:none; width:var(--gn-character-size); height:var(--gn-character-size); }
.gn-character-body {
  --gn-character-shade:color-mix(in srgb,var(--gn-character-color),#000 8%);
  display:flex; align-items:center; position:relative; box-sizing:border-box; width:100%; height:100%;
  padding:0 calc(var(--gn-character-size)*.32);
  border-radius:var(--gn-character-radius) var(--gn-character-radius) 0 var(--gn-character-radius);
  background:linear-gradient(var(--gn-character-light,145deg),color-mix(in srgb,var(--gn-character-color),#fff 6%),var(--gn-character-color) 45%,var(--gn-character-shade) 85%);
  /* 沿身体与短尾的合成轮廓描边，避免矩形 outline 横穿尾巴根部。 */
  filter:drop-shadow(1px 0 0 var(--gn-character-edge,transparent)) drop-shadow(-1px 0 0 var(--gn-character-edge,transparent)) drop-shadow(0 1px 0 var(--gn-character-edge,transparent)) drop-shadow(0 -1px 0 var(--gn-character-edge,transparent)) drop-shadow(0 calc(var(--gn-character-size)*(.035 + .061*var(--gn-character-lift,0))) calc(var(--gn-character-size)*(.1 + .083*var(--gn-character-lift,0))) color-mix(in srgb,#1c1b1a calc(8% + 4%*var(--gn-character-lift,0)),transparent));
  transform-origin:50% 107.5%;
}
.gn-character-body::before { content:""; position:absolute; inset:0; border-radius:inherit; box-shadow:inset 0 1px 1px #ffffff26; mask:linear-gradient(#000,transparent 60%); pointer-events:none; }
.gn-character-body::after {
  content:""; position:absolute; right:0; bottom:calc(var(--gn-character-size)*-.075);
  width:calc(var(--gn-character-size)*.18); height:calc(var(--gn-character-size)*.18);
  background:var(--gn-character-shade);
  mask:${TAIL_SHAPE} center/100% 100% no-repeat;
}
.gn-character-eyes { display:flex; align-items:center; flex:none; gap:calc(var(--gn-character-size)*.16); }
.gn-character-eyes > i { box-sizing:border-box; flex:none; width:calc(var(--gn-character-size)*.107); height:calc(var(--gn-character-size)*.2); border-radius:100px; background:var(--gn-character-eye); }
/* 色标按角色规范：主色 90% → 提亮 15% 的 82% → 提亮 45% 的 66% → 提亮 70% 的 50%。深色面板上整体再实 2%，边缘才不沉进面板。 */
[data-color-scheme="dark"] { --gn-character-wash:2%; }
/* 水彩身体和尾巴是 ::before 上的同一个轮廓，只画一层渐变、做一次模糊：拆成两块拼接时，接缝两侧的半透明抗锯齿叠不满，会露出一条浅线。
   祖先上有 filter、opacity、mask、clip-path、backdrop-filter 时模糊只能取到空白，所以身体本身和它的容器都不能加这些属性。 */
.gn-character[data-variant="watercolor"] .gn-character-body {
  --gn-character-eye:#fffffff5;
  --gn-character-corner:min(var(--gn-character-radius),50%);
  --gn-character-tail:calc(var(--gn-character-size)*.075);
  --gn-character-stops:color-mix(in srgb,var(--gn-character-color) calc(90% + var(--gn-character-wash,0%)),transparent),color-mix(in srgb,color-mix(in srgb,var(--gn-character-color),#fff 15%) calc(82% + var(--gn-character-wash,0%)),transparent) 30%,color-mix(in srgb,color-mix(in srgb,var(--gn-character-color),#fff 45%) calc(66% + var(--gn-character-wash,0%)),transparent) 62%,color-mix(in srgb,color-mix(in srgb,var(--gn-character-color),#fff 70%) calc(50% + var(--gn-character-wash,0%)),transparent);
  background:none; filter:none;
}
/* 轮廓用遮罩裁，别换回 clip-path：祖先有带圆角的 overflow 裁剪时（如后台设置页的预览框），Chrome 的 clip-path 裁不住背景模糊，身后露出方块；
   这层裁剪里的祖先再跑完一次透明度过渡，连身体也不裁了，整只变方。底边框把绘制区域延伸到尾巴，第一层遮罩铺满按圆角裁出的内边距盒（即身体），
   左下角的纵向半径因此要加上底边框高度；第二层是实色款的尾巴。 */
.gn-character[data-variant="watercolor"] .gn-character-body::before {
  bottom:calc(var(--gn-character-tail)*-1); border-bottom:var(--gn-character-tail) solid transparent;
  border-radius:var(--gn-character-corner) var(--gn-character-corner) 0 var(--gn-character-corner) / var(--gn-character-corner) var(--gn-character-corner) 0 calc(var(--gn-character-corner) + var(--gn-character-tail));
  box-shadow:none;
  mask:linear-gradient(#000 0 0) padding-box, ${TAIL_SHAPE} 100% 100%/calc(var(--gn-character-size)*.18) calc(var(--gn-character-size)*.18) no-repeat border-box;
  background:radial-gradient(circle farthest-corner at 50% calc((100% - var(--gn-character-tail))*.56),var(--gn-character-stops)) border-box;
  -webkit-backdrop-filter:blur(10px) saturate(1.3); backdrop-filter:blur(10px) saturate(1.3);
}
.gn-character[data-variant="watercolor"] .gn-character-body::after { content:none; }
/* 拉长身体时浓色只留在眼睛后面，到正文处过渡成几乎不透明的浅底配深色字：半透明浅色上的白字在花背景里看不清。 */
.gn-character[data-variant="watercolor"][data-prompt] .gn-character-body {
  --gn-character-corner:min(var(--gn-character-prompt-radius),50%);
  --gn-character-stops:color-mix(in srgb,var(--gn-character-color) 90%,transparent),color-mix(in srgb,color-mix(in srgb,var(--gn-character-color),#fff 15%) 86%,transparent) 30%,color-mix(in srgb,color-mix(in srgb,var(--gn-character-color),#fff 45%) 88%,transparent) 62%,color-mix(in srgb,color-mix(in srgb,var(--gn-character-color),#fff 85%) 94%,transparent);
}
.gn-character[data-variant="watercolor"][data-prompt] .gn-character-body::before {
  background:radial-gradient(circle calc(var(--gn-character-size)*1.2) at calc(var(--gn-character-size)*.5) calc((100% - var(--gn-character-tail))*.56),var(--gn-character-stops)) border-box;
}
.gn-character[data-variant="watercolor"] .gn-character-copy { --gn-character-ink:color-mix(in srgb,var(--gn-character-color),#000 65%); }
/* 定位后的 ::before 会盖住未定位的眼睛。 */
.gn-character[data-variant="watercolor"] .gn-character-eyes { position:relative; }
.gn-character[data-prompt] { display:block; position:relative; width:100%; height:auto; min-height:var(--gn-character-size); }
.gn-character[data-prompt] .gn-character-body { position:absolute; right:0; top:0; border-radius:var(--gn-character-prompt-radius) var(--gn-character-prompt-radius) 0 var(--gn-character-prompt-radius); }
[data-position="left"] .gn-character[data-prompt] .gn-character-body { right:auto; left:0; }
/* 生成中的扫光字默认按面板底色取灰，压在主题色身体上看不清，改用正文色。 */
.gn-character-copy { --gn-character-ink:var(--gn-character-eye); --gn-shimmer-base:color-mix(in srgb,var(--gn-character-ink) 60%,transparent); --gn-shimmer-highlight:var(--gn-character-ink); display:block; position:relative; width:100%; min-height:var(--gn-character-size); padding:14px 52px 14px 70px; border:0; border-radius:var(--gn-character-prompt-radius); background:transparent; color:var(--gn-character-ink); cursor:pointer; font:inherit; text-align:start; line-height:1.45; white-space:pre-wrap; overflow-wrap:anywhere; }
/* 原型以宽度展开身体，保留尾巴随边缘移动；正文独立排版，展开中不反复换行。 */
@keyframes gnCharacterExpand { from { width:var(--gn-character-size); } to { width:100%; } }
@keyframes gnCharacterCopy { from { opacity:0; } to { opacity:1; } }
@keyframes gnCharacterIdle {
  0%,13%,32%,50%,70%,79%,90%,100% { transform:none; }
  7% { transform:scale(1.012,.988); }
  17%,26% { transform:translateX(calc(var(--gn-character-size)*-.012)) rotate(-2.5deg) scale(1.012,.988); }
  54%,64% { transform:translateX(calc(var(--gn-character-size)*.012)) rotate(3deg) scale(.985,1.015); }
  82% { transform:scale(1.025,.975); }
  86% { transform:scale(.992,1.008); }
}
@keyframes gnCharacterGaze {
  0%,11%,34%,47%,70%,79%,90%,100% { transform:none; }
  7% { transform:scale(.9881,1.0121); }
  14%,26% { transform:translate(calc(var(--gn-character-size)*-.075),calc(var(--gn-character-size)*-.015)) scale(.9881,1.0121); }
  50%,64% { transform:translate(calc(var(--gn-character-size)*.08),calc(var(--gn-character-size)*-.035)) scale(1.0152,.9852); }
  82% { transform:translateY(calc(var(--gn-character-size)*.012)) scale(.9756,1.0256); }
  86% { transform:translateY(calc(var(--gn-character-size)*-.006)) scale(1.0081,.9921); }
}
@keyframes gnCharacterBlink {
  0%,18%,19.6%,70.5%,72.1%,74%,75.6%,100% { transform:scaleY(1); }
  18.8%,71.3%,74.8% { transform:scaleY(.1); }
}
@keyframes gnCharacterBreathe { 0%,100% { transform:none; } 50% { transform:scale(1.012,.988); } }
@keyframes gnCharacterQuietEyes { 0%,100% { transform:none; } 50% { transform:scale(.9881,1.0121); } }
.gn-character[data-motion="working"] .gn-character-body { transform:scale(1.04,.96); }
.gn-character[data-motion="working"] .gn-character-eyes { transform:translate(calc(var(--gn-character-size)*-.085),calc(var(--gn-character-size)*-.12)) scale(.9615,1.0417); }
.gn-character[data-motion="working"] .gn-character-eyes > i { width:calc(var(--gn-character-size)*.0859375); height:calc(var(--gn-character-size)*.15625); }
.gn-character[data-motion="waiting"] .gn-character-body { transform:rotate(5deg) scale(.985,1.015); }
.gn-character[data-motion="waiting"] .gn-character-eyes { transform:translateY(calc(var(--gn-character-size)*-.015)) scale(1.0152,.9852); }
.gn-character[data-motion="waiting"] .gn-character-eyes > i { width:calc(var(--gn-character-size)*.125); height:calc(var(--gn-character-size)*.125); border-radius:50%; }
/* 睡着：说完那句就定格，整体半透明、不模糊背后，眼睛闭成两道细线。整体透明度本来就让模糊取不到背景，这里一并关掉省开销。
   买家发出下一句时，上一组的角色就地 200ms 合眼降到 50%（设计稿 11a）。 */
.gn-character[data-motion="asleep"] { opacity:.5; transition:opacity .2s ease; }
.gn-character:is([data-motion="asleep"],[data-motion="dozing"]) .gn-character-body::before { -webkit-backdrop-filter:none; backdrop-filter:none; }
.gn-character[data-motion="asleep"] .gn-character-eyes { margin-top:calc(var(--gn-character-size)*.083); transition:margin-top .2s ease; }
.gn-character[data-motion="asleep"] .gn-character-eyes > i { width:calc(var(--gn-character-size)*.167); height:calc(var(--gn-character-size)*.0625); border-radius:1px; transition:width .2s ease, height .2s ease, border-radius .2s ease; }
/* 说话：回复逐字流出时眼睛睁圆、不眨眼（设计稿 11a）。 */
.gn-character[data-motion="speaking"] .gn-character-eyes > i { width:calc(var(--gn-character-size)*.146); height:calc(var(--gn-character-size)*.229); }
/* 打盹：买家点了跳转、页面还没换走（设计稿 12a），只降到 70%，200ms 合眼。合眼的尺寸和位移要与 animations.ts 里 gnWakeEyes 的起点一致，新页面醒来才接得上。 */
.gn-character[data-motion="dozing"] { opacity:.7; transition:opacity .2s ease; }
.gn-character[data-motion="dozing"] .gn-character-eyes > i { width:calc(var(--gn-character-size)*.167); height:calc(var(--gn-character-size)*.0625); border-radius:1px; transform:translateY(calc(var(--gn-character-size)*.083)); transition:width .2s ease, height .2s ease, border-radius .2s ease, transform .2s ease; }
/* 失败：斜短眼，侧倾一下后保持。眼睛变宽后往左挪回一半，和竖眼同一个中心（原型居中排眼睛，这里靠左排）。 */
.gn-character[data-motion="blocked"] .gn-character-eyes { margin-left:calc(var(--gn-character-size)*-.043); }
.gn-character[data-motion="blocked"] .gn-character-eyes > i { width:calc(var(--gn-character-size)*.15); height:calc(var(--gn-character-size)*.055); transform:rotate(18deg); }
.gn-character[data-motion="blocked"] .gn-character-eyes > i:first-child { transform:rotate(-18deg); }
@keyframes gnCharacterWorkBody {
  0%,12%,100% { transform:scale(1.04,.96); }
  24%,38% { transform:rotate(-1.5deg) scale(1.045,.955); }
  48%,84% { transform:scale(1.01,.99); }
  62%,74% { transform:rotate(1.5deg) scale(1.04,.96); }
}
@keyframes gnCharacterWorkEyes {
  0%,12%,88%,100% { transform:translate(calc(var(--gn-character-size)*-.085),calc(var(--gn-character-size)*-.12)) scale(.9615,1.0417); }
  20%,36% { transform:translate(calc(var(--gn-character-size)*-.105),calc(var(--gn-character-size)*-.10)) scale(.9569,1.0471); }
  50%,72% { transform:translate(calc(var(--gn-character-size)*.10),calc(var(--gn-character-size)*-.14)) scale(.9615,1.0417); }
}
/* 思考（生成中）：抬眼看右上 → 往左微移 → 眨一下 → 回正，4 秒一轮（设计稿 11a）。
   位移照设计稿消息旁头像上的 2px / 3px，按 24px 换算成比例，入口上的大角色等比放大。 */
@keyframes gnCharacterLook {
  0%,6%,80%,100% { transform:none; }
  14%,30% { transform:translate(calc(var(--gn-character-size)*.083),calc(var(--gn-character-size)*-.125)) scaleY(1.1); }
  36%,50%,58%,72% { transform:translate(calc(var(--gn-character-size)*-.042),calc(var(--gn-character-size)*-.125)) scaleY(1.1); }
  54% { transform:translate(calc(var(--gn-character-size)*-.042),calc(var(--gn-character-size)*-.125)) scaleY(.12); }
}
@keyframes gnCharacterListen {
  0% { transform:scale(1.025,.975); }
  45% { transform:rotate(-2deg) scale(.98,1.02); }
  100% { transform:rotate(5deg) scale(.985,1.015); }
}
@keyframes gnCharacterListenBlink { 0%,86%,90%,100% { transform:scaleY(1); } 88% { transform:scaleY(.15); } }
@keyframes gnCharacterAckBody { 0%,100% { transform:none; } 24% { transform:scale(1.04,.96); } 56% { transform:translateY(calc(var(--gn-character-size)*-.025)) scale(.98,1.02); } 80% { transform:scale(1.008,.992); } }
@keyframes gnCharacterAckEyes { 0%,100% { transform:none; } 24% { transform:translateY(calc(var(--gn-character-size)*.012)) scale(.9615,1.0417); } 56% { transform:translateY(calc(var(--gn-character-size)*-.01)) scale(1.0204,.9804); } 80% { transform:translateY(calc(var(--gn-character-size)*-.025)) scale(.9921,1.0081); } }
@keyframes gnCharacterDoneBody { 0%,100% { transform:none; } 20% { transform:scale(1.05,.95); } 48% { transform:translateY(calc(var(--gn-character-size)*-.05)) scale(.975,1.025); } 76% { transform:scale(1.02,.98); } }
@keyframes gnCharacterDoneEyes { 0%,100% { transform:none; } 20% { transform:translateY(calc(var(--gn-character-size)*.015)) scale(.9524,1.0526); } 48% { transform:translateY(calc(var(--gn-character-size)*-.012)) scale(1.0256,.9756); } 76% { transform:translateY(calc(var(--gn-character-size)*-.025)) scale(.9804,1.0204); } }
/* 笑眼写成动画而不是静态规则：反应要等回复行淡入完才归位，写成静态会让笑眼多挂将近一秒。 */
@keyframes gnCharacterSmileSpacing { from,to { gap:calc(var(--gn-character-size)*.140625); margin-left:calc(var(--gn-character-size)*-.0551875); } }
@keyframes gnCharacterSmile { from,to { width:calc(var(--gn-character-size)*.171875); height:calc(var(--gn-character-size)*.0859375); border:calc(var(--gn-character-size)*.046875) solid var(--gn-character-eye); border-bottom:0; border-radius:12px 12px 0 0; background:transparent; } }
@keyframes gnCharacterAttentionBody { 0%,100% { transform:none; } 45% { transform:rotate(-3deg) scale(1.025,.975); } }
@keyframes gnCharacterAttentionEyes { 0%,100% { transform:none; } 45% { transform:scale(.9756,1.0256); } }
@media (prefers-reduced-motion: reduce) {
  .gn-character[data-motion="done"] .gn-character-eyes { gap:calc(var(--gn-character-size)*.140625); margin-left:calc(var(--gn-character-size)*-.0551875); }
  .gn-character[data-motion="done"] .gn-character-eyes > i { width:calc(var(--gn-character-size)*.171875); height:calc(var(--gn-character-size)*.0859375); border:calc(var(--gn-character-size)*.046875) solid var(--gn-character-eye); border-bottom:0; border-radius:12px 12px 0 0; background:transparent; }
}
@media (prefers-reduced-motion: no-preference) {
  /* 一次性动作跟着所在消息行的入场延迟起播，否则行还没淡入就演完了；收起入口没有这个延迟。 */
  .gn-character[data-motion="ack"] .gn-character-body { animation:gnCharacterAckBody 350ms var(--gn-enter-delay,0ms) cubic-bezier(.23,1,.32,1) both; }
  .gn-character[data-motion="ack"] .gn-character-eyes { animation:gnCharacterLook 4s ease-in-out infinite, gnCharacterAckEyes 350ms var(--gn-enter-delay,0ms) cubic-bezier(.23,1,.32,1) backwards; }
  .gn-character[data-motion="done"] .gn-character-body { animation:gnCharacterDoneBody ${String(CHARACTER_DONE_MS)}ms var(--gn-enter-delay,0ms) cubic-bezier(.23,1,.32,1) both; }
  .gn-character[data-motion="done"] .gn-character-eyes { animation:gnCharacterDoneEyes ${String(CHARACTER_DONE_MS)}ms var(--gn-enter-delay,0ms) cubic-bezier(.23,1,.32,1) both, gnCharacterSmileSpacing ${String(CHARACTER_DONE_MS)}ms var(--gn-enter-delay,0ms) backwards; }
  .gn-character[data-motion="done"] .gn-character-eyes > i { animation:gnCharacterSmile ${String(CHARACTER_DONE_MS)}ms var(--gn-enter-delay,0ms) backwards; }
  .gn-character[data-motion="blocked"] .gn-character-body { animation:gnCharacterAttentionBody 300ms var(--gn-enter-delay,0ms) cubic-bezier(.23,1,.32,1) both; }
  .gn-character[data-motion="blocked"] .gn-character-eyes { animation:gnCharacterAttentionEyes 300ms var(--gn-enter-delay,0ms) cubic-bezier(.23,1,.32,1) both; }
  .gn-character[data-prompt] .gn-character-body { animation:gnCharacterExpand 400ms cubic-bezier(.77,0,.175,1) both; }
  .gn-character[data-prompt] .gn-character-copy { animation:gnCharacterCopy 200ms 200ms cubic-bezier(.23,1,.32,1) both; }
  .gn-character[data-motion="idle"] .gn-character-body { animation:gnCharacterIdle 10s cubic-bezier(.77,0,.175,1) infinite; }
  .gn-character[data-motion="idle"] .gn-character-eyes { animation:gnCharacterGaze 10s cubic-bezier(.77,0,.175,1) infinite; }
  .gn-character[data-motion="quiet"] .gn-character-body { animation:gnCharacterBreathe 10s ease-in-out infinite; }
  .gn-character[data-motion="quiet"] .gn-character-eyes { animation:gnCharacterQuietEyes 10s ease-in-out infinite; }
  .gn-character:is([data-motion="idle"],[data-motion="quiet"]) .gn-character-eyes > i { animation:gnCharacterBlink 10s linear infinite; }
  .gn-character[data-motion="working"] .gn-character-body { animation:gnCharacterWorkBody 3.2s cubic-bezier(.77,0,.175,1) infinite; }
  .gn-character[data-motion="working"] .gn-character-eyes { animation:gnCharacterWorkEyes 3.2s cubic-bezier(.77,0,.175,1) infinite; }
  .gn-character[data-motion="looking"] .gn-character-eyes { animation:gnCharacterLook 4s ease-in-out infinite; }
  .gn-character[data-motion="waiting"] .gn-character-body { animation:gnCharacterListen 450ms cubic-bezier(.23,1,.32,1) both; }
  .gn-character[data-motion="waiting"] .gn-character-eyes > i { animation:gnCharacterListenBlink 6s linear infinite; }
  .gn-character[data-gazing] :is(.gn-character-body,.gn-character-eyes) { animation:none; }
  .gn-character[data-gazing] .gn-character-body { transform-origin:center; transition:transform 180ms ease-out; }
}
`;
